// Всплывающая панель под кнопкой (components/Dropdown/README.md) для выбора, которому мало
// списка: период «с — по» с быстрыми пилюлями. Esc и клик снаружи закрывают, фокус
// возвращается на кнопку — если страница не перевела его сама, пока панель закрывалась
// (переход из ленты уведомлений на строку заказа).
//
// 011: панель можно прикрепить к элементу страницы без кнопки-триггера — пометке в ячейке
// таблицы протокола, которую рисует не React (anchor). Тогда фокус после закрытия возвращает
// вызывающий.
import * as RadixPopover from '@radix-ui/react-popover';
import { useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { relayEscape } from '../lib/layers.ts';
import { focusMovedOn, useExitFocus } from '../lib/return-focus.ts';

export interface PopoverProps {
  /** Кнопка, которая открывает панель; должна принимать ref и обработчики. */
  trigger?: ReactElement;
  /** Элемент, к которому прикреплена панель, если кнопки-триггера нет. */
  anchor?: HTMLElement | null;
  /** Имя панели для программ чтения экрана. */
  label: string;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  className?: string;
  /**
   * Фокус при открытии — на саму панель, а не на первый элемент: если у первого элемента есть
   * подсказка, она не всплывает сама и первый Esc закрывает панель (приёмка 013, «Столбцы»).
   */
  focusPanel?: boolean;
}

export function Popover({
  trigger,
  anchor,
  label,
  children,
  open,
  onOpenChange,
  align = 'start',
  className,
  focusPanel = false,
}: PopoverProps) {
  const virtualRef = useMemo(() => ({ current: anchor ?? null }), [anchor]);
  const panelRef = useRef<HTMLDivElement>(null);
  // Открыта ли панель — и у управляемой, и у своей: по смене видно начало выхода (044).
  const [ownOpen, setOwnOpen] = useState(false);
  const shown = open ?? ownOpen;
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Фокус на кнопку — в начале выхода (MOT-06). Без кнопки-триггера фокус возвращает вызывающий;
  // фокус, который страница перевела сама, не перебивается.
  const exitFocus = useExitFocus(shown, {
    triggerRef,
    contentRef: panelRef,
    onCloseAutoFocus: (event) => {
      if (!trigger || focusMovedOn(panelRef.current)) event.preventDefault();
    },
  });
  return (
    <RadixPopover.Root
      open={shown}
      onOpenChange={(next) => {
        if (open === undefined) setOwnOpen(next);
        onOpenChange?.(next);
      }}
    >
      {trigger ? (
        <RadixPopover.Trigger asChild ref={triggerRef}>
          {trigger}
        </RadixPopover.Trigger>
      ) : null}
      {anchor ? <RadixPopover.Anchor virtualRef={virtualRef} /> : null}
      <RadixPopover.Portal>
        <RadixPopover.Content
          ref={panelRef}
          aria-label={label}
          align={align}
          sideOffset={4}
          collisionPadding={8}
          className={cx('t-pop kp-pop kp-popover', className)}
          {...(focusPanel
            ? {
                tabIndex: -1,
                onOpenAutoFocus: (event: Event) => {
                  event.preventDefault();
                  panelRef.current?.focus();
                },
              }
            : {})}
          onInteractOutside={exitFocus.onInteractOutside}
          onCloseAutoFocus={exitFocus.onCloseAutoFocus}
          // Esc, пока панель уходит, — следующему слою (044, MOT-07).
          onEscapeKeyDown={(event) => {
            if (shown) return;
            event.preventDefault();
            relayEscape(panelRef.current, event);
          }}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
